/**
 * Runtime monitoring for the self-hosted Inter Variable rollout.
 *
 * Captures three classes of regression that don't surface in standard
 * Core Web Vitals:
 *   1. Font file failed to download / parse (FontFace promise rejects).
 *   2. Fonts took unusually long to become ready, meaning users sat in
 *      the system fallback for an extended FOUT window.
 *   3. Layout shifts whose nearest source happens to live inside a text
 *      node — a heuristic signal that a late font swap (or a missing
 *      preload) is causing CLS we'd otherwise miss.
 *
 * All telemetry is best-effort and silently no-ops on the server, on
 * unsupported browsers, and on any exception path — typography
 * instrumentation must never break the page.
 */

import { recordWebVital } from "@/lib/rum.functions";

type FontIssue =
  | { kind: "font_load_failed"; family: string; error: string }
  | { kind: "font_ready_slow"; ms: number; count: number }
  | { kind: "font_layout_shift"; value: number; sources: number };

// Threshold above which we treat the FOUT period as a regression worth
// logging. 800ms picks up CDN miss / cold-cache scenarios while
// ignoring normal warm-cache loads.
const SLOW_FONTS_READY_MS = 800;
// Single-shift CLS values above this are worth flagging individually
// when a text-bearing node is implicated.
const TEXT_SHIFT_THRESHOLD = 0.05;

function report(issue: FontIssue) {
  try {
    // Reuse the RUM pipeline so font issues land alongside Web Vitals.
    // We stash the issue kind in `name` (cast through the union the
    // server already accepts) and the magnitude in `value`. Anything
    // the server doesn't recognise is dropped server-side; logging
    // here remains safe.
    void recordWebVital({
      data: {
        name: ("FONT_" + issue.kind.toUpperCase()) as unknown as "LCP",
        value:
          issue.kind === "font_ready_slow"
            ? issue.ms
            : issue.kind === "font_layout_shift"
              ? Math.round(issue.value * 10000) / 10000
              : 1,
        rating: "needs-improvement",
        id:
          issue.kind === "font_load_failed"
            ? issue.family
            : Math.random().toString(36).slice(2),
        path:
          typeof window !== "undefined" ? window.location.pathname : "/",
        navigationType: "navigate",
        ua:
          typeof navigator !== "undefined"
            ? navigator.userAgent.slice(0, 400)
            : "",
      },
    });
  } catch {
    // RUM must never break the page.
  }
  if (typeof console !== "undefined") {
    // Surface in DevTools too — useful when triaging locally.
    console.warn("[font-monitor]", issue);
  }
}

export function initFontMonitoring() {
  if (typeof window === "undefined") return;
  if (typeof document === "undefined" || !("fonts" in document)) return;

  const start =
    typeof performance !== "undefined" ? performance.now() : Date.now();

  // 1) Track ready time + count.
  try {
    document.fonts.ready
      .then((set) => {
        const elapsed =
          (typeof performance !== "undefined"
            ? performance.now()
            : Date.now()) - start;
        if (elapsed > SLOW_FONTS_READY_MS) {
          report({
            kind: "font_ready_slow",
            ms: Math.round(elapsed),
            count: set.size,
          });
        }
      })
      .catch(() => {
        /* ignored */
      });
  } catch {
    /* ignored */
  }

  // 2) Per-FontFace load failures.
  try {
    document.fonts.addEventListener("loadingerror", (event: Event) => {
      const ev = event as Event & { fontfaces?: FontFace[] };
      const faces = ev.fontfaces ?? [];
      for (const f of faces) {
        report({
          kind: "font_load_failed",
          family: f.family || "unknown",
          error: f.status || "error",
        });
      }
      if (faces.length === 0) {
        report({
          kind: "font_load_failed",
          family: "unknown",
          error: "loadingerror",
        });
      }
    });
  } catch {
    /* ignored */
  }

  // 3) Heuristic: layout shifts whose source is a text-bearing element.
  //    Strong signal that a late font swap is moving content around.
  try {
    if ("PerformanceObserver" in window) {
      const obs = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          const ls = entry as PerformanceEntry & {
            value?: number;
            hadRecentInput?: boolean;
            sources?: Array<{ node?: Node }>;
          };
          if (ls.hadRecentInput) continue;
          if (!ls.value || ls.value < TEXT_SHIFT_THRESHOLD) continue;
          const sources = ls.sources ?? [];
          const textSources = sources.filter((s) => {
            const node = s.node as (Node & { textContent?: string }) | undefined;
            if (!node) return false;
            const text = (node.textContent || "").trim();
            return text.length > 0;
          });
          if (textSources.length > 0) {
            report({
              kind: "font_layout_shift",
              value: ls.value,
              sources: textSources.length,
            });
          }
        }
      });
      obs.observe({ type: "layout-shift", buffered: true } as PerformanceObserverInit);
    }
  } catch {
    /* ignored */
  }
}
